import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, debounce, debounceTime, filter, firstValueFrom, interval, map, Observable, of } from 'rxjs';
import { State } from 'app/reducers';
import { getRouterBandId, getIsOnBandSelectionPage } from 'app/reducers/router.reducer';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Location } from '@angular/common';
import { ROUTE_INDEX } from './route-index';
import { DeskTaskService } from './core/desk/desk-task.service';

export interface NavigationItem {
    label: string;
    routerLink: string;
    icon: string;
    children?: NavigationItem[];
    newsCount?: Observable<number>;
}

@Injectable({ providedIn: 'root' })
export class NavigationService {

    private readonly animationState$$ = new BehaviorSubject<'slideLeft' | 'slideRight'>(null);
    public animationState$ = this.animationState$$.asObservable();
    private readonly deskTaskService = inject(DeskTaskService);


    constructor(
        private store: Store<State>,
        private router: Router,
        private location: Location,
        private activatedRoute: ActivatedRoute,
    ) {
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                const newPath = event.url.replace(/^\/?app\/wrapper\/?/, '');
                this.setAnimationDirectionByRouteChange(newPath);
            });
    }

    getMainNavigationItems(): Observable<NavigationItem[]> {
        const bandId$ = this.store.select(getRouterBandId); // Get the current bandId dynamically
        const isOnBandSelectionPage$ = this.store.select(getIsOnBandSelectionPage); // Check if we are on the band selection page

        return combineLatest([bandId$, isOnBandSelectionPage$]).pipe(
            map(([bandId, isOnBandSelectionPage]) => {
                const bandPath = bandId ? `band/${bandId}` : 'band';

                return [
                    {
                        label: 'APP.DESK',
                        routerLink: 'desk',
                        icon: 'pi pi-fw icon-desk',
                        children: [
                            {
                                label: 'Dashboard',
                                routerLink: 'desk/dashboard',
                                icon: 'pi pi-fw pi-home',
                            },
                            {
                                label: 'Chat',
                                routerLink: 'desk/chat',
                                icon: 'pi pi-fw pi-comment',
                                newsCount: interval(5000).pipe(
                                    debounceTime(300),
                                    map(() => 1 + Math.floor(Math.random() * 10))
                                )
                            },
                            {
                                label: 'Tasks',
                                routerLink: 'desk/desk-task',
                                icon: 'pi pi-fw pi-check-square',
                                newsCount: this.deskTaskService.totalPendingCount$
                            },
                            {
                                label: 'Calendar',
                                routerLink: 'desk/calendar',
                                icon: 'pi pi-fw pi-calendar',
                            },
                        ],
                    },
                    {
                        label: 'APP.BANDS',
                        routerLink: 'band',
                        icon: 'pi pi-fw pi-users',
                        children: [
                            ...(bandId
                                ? [
                                    // {
                                    //     label: 'Dashboard',
                                    //     routerLink: `${bandPath}/dashboard`,
                                    //     icon: 'pi pi-fw pi-home',
                                    // },
                                    {
                                        label: 'Assets',
                                        routerLink: `${bandPath}/assets`,
                                        icon: 'pi pi-fw pi-warehouse',
                                        // children: [
                                        //     {
                                        //         label: 'Setlists',
                                        //         routerLink: `${bandPath}/assets/setlists`,
                                        //         icon: 'pi pi-fw icon-setlist',
                                        //     },
                                        //     {
                                        //         label: 'Equipment',
                                        //         routerLink: `${bandPath}/assets/equipment`,
                                        //         icon: 'pi pi-fw icon-equipment',
                                        //     },
                                        //     {
                                        //         label: 'Motor Pool',
                                        //         routerLink: `${bandPath}/assets/motor-pool`,
                                        //         icon: 'pi pi-fw icon-car-3',
                                        //     },
                                        //     {
                                        //         label: 'Locations',
                                        //         routerLink: `${bandPath}/assets/location`,
                                        //         icon: 'pi pi-fw pi-map',
                                        //     }
                                        // ],
                                    },
                                    {
                                        label: 'Calendar',
                                        routerLink: `${bandPath}/calendar`,
                                        icon: 'pi pi-fw pi-calendar',
                                    },
                                    {
                                        label: 'Tasks',
                                        routerLink: `${bandPath}/tasks`,
                                        icon: 'pi pi-fw pi-check-square',
                                    },
                                    // {
                                    //     label: 'Email',
                                    //     routerLink: `${bandPath}/email`,
                                    //     icon: 'pi pi-fw pi-envelope'
                                    // },

                                    {
                                        label: 'Settings',
                                        routerLink: `${bandPath}/settings`,
                                        icon: 'pi pi-fw pi-cog',

                                    }
                                ]
                                : []),
                        ],
                    }, {
                        label: 'APP.TOOLS',
                        routerLink: 'tools',
                        icon: 'pi pi-fw pi-wrench',
                        children: [
                            {
                                label: 'Tuner',
                                routerLink: 'tools/tuner',
                                icon: 'pi pi-fw icon-tuning-fork',
                            },
                            {
                                label: 'Metronome',
                                routerLink: 'tools/metronome',
                                icon: 'pi pi-fw icon-metronome',
                            },
                            {
                                label: 'Scale Helper',
                                routerLink: 'tools/scale-helper',
                                icon: 'pi pi-fw icon-clefs-TEST',
                            },
                            {
                                label: 'Decibel Meter',
                                routerLink: 'tools/decibel-meter',
                                icon: 'pi pi-fw icon-loudness-meter',

                            },
                            {
                                label: 'Audio Converter',
                                routerLink: 'tools/audio-converter',
                                icon: 'pi pi-fw pi-file',
                            }
                        ],
                    },
                    {
                        label: 'APP.APP_SETTINGS',
                        routerLink: 'settings',
                        icon: 'pi pi-fw pi-cog',
                        children: [
                            {
                                label: 'App Settings',
                                routerLink: 'settings/app-settings',
                                icon: 'pi pi-fw pi-cog',
                            },
                            {
                                label: 'User Settings',
                                routerLink: 'settings/user-settings',
                                icon: 'pi pi-fw pi-user',
                            },
                            {
                                label: 'Theming',
                                routerLink: 'settings/theming',
                                icon: 'pi pi-fw pi-palette',
                            },
                            {
                                label: 'Export',
                                routerLink: 'settings/ical-export',
                                icon: 'pi pi-fw pi-file-export',
                            }
                        ],
                    }
                ];
            })
        );
    }

    navigateBySwipe(direction: 'left' | 'right') {
        const currentUrl = this.router.url.replace(/^\/?app\/wrapper\/?/, '');
        const currentSegments = currentUrl.split('/');
        const currentRoute = ROUTE_INDEX.find(template => {
            const tplSegments = template.path.split('/');
            if (tplSegments.length !== currentSegments.length) return false;
            return tplSegments.every((seg, i) => seg.startsWith(':') || seg === currentSegments[i]);
        });


        if (!currentRoute) {
            console.warn('No matching route found in route-index.ts for current URL:', currentUrl);
            return;
        }

        let targetRoute = null;
        if (direction === 'left' && currentRoute.allowSwipeLeft) {
            targetRoute = currentRoute.prev
        } else if (direction === 'right' && currentRoute.allowSwipeRight) {
            targetRoute = currentRoute.next
        }

        if (!targetRoute) return;
        const targetSegments = targetRoute.split('/');
        const params: Record<string, string> = {};
        currentRoute.path.split('/').forEach((seg, i) => {
            if (seg.startsWith(':')) {
                params[seg.slice(1)] = currentSegments[i];
            }
        })

        const finalPath = targetSegments
            .map(seg => seg.startsWith(':') ? params[seg.slice(1)] ?? '' : seg)
            .join('/');

        if (!finalPath) return;

        const animationDirection = direction === 'left' ? 'slideLeft' : 'slideRight';
        this.animationState$$.next(animationDirection);

        this.router.navigate([`/app/wrapper/${finalPath}`], { state: { animation: direction === 'left' ? 'slideLeft' : 'slideRight' } });

    }

    private lastRoutePath = '';
    private lastDirection: 'slideLeft' | 'slideRight' | null = null;
    setAnimationDirectionByRouteChange(newPath: string) {
        const previous = this.lastRoutePath;
        this.lastRoutePath = newPath;


        const currentIndex = ROUTE_INDEX.findIndex(r => this.matchRouteAdvanced(previous, r.path));
        const newIndex = ROUTE_INDEX.findIndex(r => this.matchRouteAdvanced(newPath, r.path));

        if (newIndex === -1 || currentIndex === newIndex) {
            return;
        }

        let direction: 'slideLeft' | 'slideRight' =
            newIndex > currentIndex ? 'slideLeft' : 'slideRight';

        this.lastDirection = direction;

        if (!direction) {
            direction = 'slideRight';
        }

        this.animationState$$.next(direction);
        setTimeout(() => {
            this.animationState$$.next(null);
        }, 150);
        // }, 50000)

    }

    private matchRouteAdvanced(path: string, template: string): boolean {
        const pathSegments = path.split('/');
        const templateSegments = template.split('/');

        if (templateSegments.length !== pathSegments.length) return false;

        for (let i = 0; i < templateSegments.length; i++) {
            const templateSeg = templateSegments[i];
            const pathSeg = pathSegments[i];

            if (templateSeg.startsWith(':')) continue;
            if (templateSeg !== pathSeg) return false;
        }

        return true;
    }

}


